<template>
  <div
    class="product-card"
    :class="{
      [$class('loading')]: !product,
      'show-secondary-image': showSecondaryImage,
      'product-card--has-badge': productBadge,
    }"
    role="group"
    :aria-label="$string('product.card.product')"
    @pointerenter="handleCardHover(true)"
    @pointerleave="handleCardHover(false)"
  >
    <div class="product-carousel">
      <p
        v-if="productBadge"
        class="product-card__badge text-other-label-s"
        :style="{ color: productBadge.color, borderColor: productBadge.color }"
        v-text="productBadge.name"
      />
      <div
        class="product-carousel__track-container"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <div
          class="product-carousel__track"
          :class="{ 'no-transition': !isTransitioning }"
          :style="{
            transform: `translateX(-${sortedMedia.length > 1
              ? (currentSlide + 1) * 100 : currentSlide * 100}%)`
          }"
          @transitionend="handleTransitionEnd"
        >
          <!-- Clone of last slide -->
          <div
            v-if="sortedMedia.length > 1"
            :key="`media-clone-last-${product.handle}`"
            class="product-carousel__slide"
          >
            <a
              :href="product.url"
              class="product-carousel__image-link"
              :aria-label="`View ${product.title}`"
            >
              <div
                class="product-carousel__image-container"
                :class="{
                  [$class('loading')]: false,
                  [$class('active')]: true,
                }"
              >
                <responsive-image
                  class="product-carousel__image"
                  :image="sortedMedia[sortedMedia.length - 1].previewImage ||
                    sortedMedia[sortedMedia.length - 1].image ||
                    sortedMedia[sortedMedia.length - 1]"
                  lazyload
                  :min-max="minMax"
                  :ratio=".69/1"
                  :sizes="sizes"
                  tabindex="-1"
                />
              </div>
            </a>
          </div>

          <!-- Original slides -->
          <div
            v-for="(mediaItem, index) of sortedMedia"
            :key="`media-${product.handle}-${activeCode}-${index}`"
            class="product-carousel__slide"
          >
            <a
              :href="product.url"
              class="product-carousel__image-link"
              :aria-label="`View ${product.title}`"
            >
              <div
                class="product-carousel__image-container"
                :class="{
                  [$class('loading')]: false,
                  [$class('active')]: true,
                }"
              >
                <responsive-image
                  class="product-carousel__image"
                  :image="mediaItem.previewImage || mediaItem.image || mediaItem"
                  :lazyload="index > 0"
                  :min-max="minMax"
                  :ratio=".69/1"
                  :sizes="sizes"
                  tabindex="-1"
                />
              </div>
            </a>
          </div>

          <!-- Clone of first slide -->
          <div
            v-if="sortedMedia.length > 1"
            :key="`media-clone-first-${product.handle}`"
            class="product-carousel__slide"
          >
            <a
              :href="product.url"
              class="product-carousel__image-link"
              :aria-label="`View ${product.title}`"
            >
              <div
                class="product-carousel__image-container"
                :class="{
                  [$class('loading')]: false,
                  [$class('active')]: true,
                }"
              >
                <responsive-image
                  class="product-carousel__image"
                  :image="sortedMedia[0].previewImage || sortedMedia[0].image || sortedMedia[0]"
                  lazyload
                  :min-max="minMax"
                  :ratio=".69/1"
                  :sizes="sizes"
                  tabindex="-1"
                />
              </div>
            </a>
          </div>
        </div>
      </div>

      <div
        v-if="sortedMedia.length > 1 && !isCarousel"
        class="product-carousel__arrows"
      >
        <button
          class="product-carousel__arrow product-carousel__arrow--prev"
          type="button"
          @click="previousSlide"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.previous')"
          />

          <plp-left-arrow />
        </button>

        <button
          class="product-carousel__arrow product-carousel__arrow--next"
          type="button"
          @click="nextSlide"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.next')"
          />

          <plp-right-arrow />
        </button>
      </div>
      <div class="product-carousel__pagination">
        <div
          v-if="!isCarousel && sortedMedia.length > 1"
          class="product-carousel__pagination-bars"
        >
          <span
            v-for="(item, idx) of sortedMedia"
            :key="`pagination-bar-${idx}`"
            role="button"
            tabindex="0"
            class="product-carousel__pagination-bar"
            :class="{
              'is-active': idx === currentSlide,
            }"
            @click="goToSlide(idx)"
            @keydown.enter="goToSlide(idx)"
            @keydown.space.prevent="goToSlide(idx)"
          />
        </div>
        <color-swatches
          v-if="colorOptions && !isCarousel"
          :color-options="colorOptions"
          :disabled-codes="disabledColorCodes"
          :featured-image-src="product.featuredImage?.src"
          is-product-card
          namespace="product-card"
          :product-handle="product.handle"
          :product-url="product.url"
          :render-plus-icon="renderPlusIcon"
          :plus-icon-count="plusIconCount"
          :variants="product.variants"
          @update-active-swatch="handleActiveSwatch"
        />
      </div>
    </div>

    <div class="product-card__footer text-center">
      <div class="product-card__title-wrapper">
        <h2
          v-show="!isHovered || !productTagline"
          class="
            product-card__title
            text-other-label-bold
          "
          v-text="productTitle"
        />
        <div
          v-if="!hideTagline && productTagline"
          v-show="isHovered"
          class="product-card__description text-body-m-regular"
          v-html="productTagline"
        />
        <p
          v-if="isLowStock && !isMobile"
          class="product-card__sold-out text-body-s-regular"
          v-text="$string('product.prices.sold_out')"
        />
      </div>
      <product-prices
        class="product-card__prices"
        :compare-at-price="compareAtPrice"
        :price="price"
        :show-discount="showDiscount"
        :show-btn-discount="showBtnDiscount"
      />
      <p
        v-if="isLowStock && isMobile"
        class="product-card__sold-out text-body-s-regular"
        v-text="$string('product.prices.sold_out')"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product card (product-card)
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean} denimCardCopy - Whether to use title & tagline overrides.
 * @param {Boolean} [hideTagline] - Hide product description.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Number} swatchLimit - Max # of swatches to render.
 *
 */
import ColorSwatches from '~async/color-swatches/color-swatches'
import ResponsiveImage from '~global/images/responsive-image'
import ProductPrices from '~global/product-prices/product-prices'
import { getDisabledColorCodes, isColorCodeInStr } from '~/helpers/product'
import PlpRightArrow from '~icons/directional-navigation/arrow/plp-right-arrow.svg'
import PlpLeftArrow from '~icons/directional-navigation/arrow/plp-left-arrow.svg'
import { mapState } from 'vuex'

import { formatProduct } from '~/format/format-product'
import productsByTagQuery from '~graphql/productsByTag.gql'

export default {
  name: 'ProductCard',

  components: {
    ColorSwatches,
    ProductPrices,
    ResponsiveImage,
    PlpRightArrow,
    PlpLeftArrow,
  },

  props: {
    denimCardCopy: {
      type: Boolean,
      default: false,
    },
    hideTagline: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: () => ({
        handle: '',
        images: [],
        media: [],
        title: '',
      }),
      required: true,
    },
    showBtnDiscount: {
      type: String,
      default: 'show',
    },
    showDiscount: {
      type: String,
      default: 'show',
    },
    minMax: {
      type: String,
      default: '300-1000',
    },
    sizes: {
      type: String,
      default: '100vw',
    },
    isCarousel: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeSwatchImage: {},
      activeVariant: {},
      initialVariant: {},
      colorOptions: this.product?.colorOptions?.[0] || [],
      currentSlide: 0,
      loadActiveSwatch: false,
      matchedProducts: [],
      renderPlusIcon: false,
      showSecondaryImage: false,
      showSwatchImage: false,
      isHovered: false,
      plusIconCount: 0,
      touchStartX: 0,
      touchEndX: 0,
      isDragging: false,
      lowStockThreshold: this.$variable('settings.product.lowStockThreshold'),
      isTransitioning: true,
    }
  },

  computed: {

    media() {
      if (this.product?.media && Array.isArray(this.product.media)) {
        return [...this.product.media]
      }
      if (this.product?.images && Array.isArray(this.product.images)) {
        return this.product.images.map((img, index) => ({
          ...img,
          type: 'image',
          index,
        }))
      }
      if (this.product?.featuredImage) {
        return [
          {
            ...this.product.featuredImage,
            type: 'image',
            index: 0,
          },
        ]
      }
      return []
    },
    shouldShowMobileLightbox() {
      if (this.isGalleryModalActive) {
        this.resetZoom()
      }
      return this.isGalleryModalActive
    },

    /**
    * Reorders the media array to ensure videos are at index 1 (second position)
    * @returns {Array} Sorted media array with videos in the second position
    */
    sortedMedia() {
      if (!this.media || this.media.length <= 1) {
        return this.media
      }
      return [...this.media]
    },

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Return the product or active variant's compare at price.
     * @returns {Boolean|Number}
     */
    compareAtPrice() {
      return this.activeVariant
        ? this.activeVariant.compareAtPrice?.amount
        : this.product?.compareAtPrice?.amount
    },

    /**
     * Get array of disabled color codes.
     * @returns {Array}
     */
    disabledColorCodes() {
      return getDisabledColorCodes(this.product?.tags || [])
    },

    /**
     * Return the product or active variant's price.
     * @returns {Boolean|Number}
     */
    price() {
      return this.activeVariant
        ? this.activeVariant.price?.amount
        : this.product?.price?.amount
    },

    /**
     * Compute product badge.
     * @returns {Boolean|String}
     */
    productBadge() {
      if (!this.product?.tags) {
        return false
      }

      const badge = this.product.tags.find((tag) => tag.includes('badge:'))
      if (!badge) {
        return false
      }
      const parts = badge.replace('badge:', '').split(':')
      return {
        name: parts[0],
        color: parts[1] || '#000000',
      }
    },

    /**
     * Compute product title to render.
     * @returns {String}
     */
    productTitle() {
      if (!this.denimCardCopy || !this.product?.denimCopyOverrides?.title) {
        return this.product?.title || ''
      }

      return this.product.denimCopyOverrides.title
    },

    /**
     * Compute product tagline to render.
     */
    productTagline() {
      if (!this.denimCardCopy || !this.product?.denimCopyOverrides?.tagline) {
        return this.product?.tagline
      }

      return this.product.denimCopyOverrides.tagline
    },

    // Show the out of stock msg on PLP
    isLowStock() {
      if (!this.product?.variants || !Array.isArray(this.product.variants)) {
        return false
      }
      const activeColorCode = this.activeVariant?.selectedOptions?.find(
        option => option.name === 'Color',
      )?.value?.split('-')[0]?.trim() ||
        this.initialVariant?.selectedOptions?.find(
          option => option.name === 'Color',
        )?.value?.split('-')[0]?.trim()

      if (!activeColorCode) {
        return false
      }
      const colorVariants = this.product.variants.filter((variant) => {
        const colorOption = variant.selectedOptions?.find(
          option => option.name === 'Color',
        )
        if (!colorOption) {
          return false
        }
        const variantColorCode = colorOption.value.split('-')[0]?.trim()
        return variantColorCode === activeColorCode
      })
      if (colorVariants.length === 0) {
        return false
      }
      const hasNoAvailableSizes = colorVariants.every((variant) => !variant.available)

      return hasNoAvailableSizes
    },

    /**
     * Style group tag.
     * @returns {String|Boolean}
     */
    styleGroupTag() {
      if (this.product?.giftCard) {
        return false
      }

      const styleGroupTag = this.product?.tags?.find(tag => tag.includes('style-group:'))

      return styleGroupTag ? styleGroupTag : false
    },
  },

  watch: {

    /**
     * Watch matched products to update swatch info.
     */
    matchedProducts() {
      this.setColorOptions()
    },

  },

  mounted() {
    this.setInitialVariant()
    this.setActiveVariant(this.initialVariant)
    this.queryMatchedProducts()
  },

  methods: {
    handleCardHover(isHovered) {
      this.isHovered = isHovered
    },

    /**
     * Navigate to previous slide
     */
    previousSlide() {
      if (!this.isTransitioning) {
        this.isTransitioning = true
      }
      this.currentSlide--
    },

    handleTransitionEnd() {
      if (this.currentSlide === -1) {
        this.isTransitioning = false
        this.currentSlide = this.sortedMedia.length - 1
      } else if (this.currentSlide === this.sortedMedia.length) {
        this.isTransitioning = false
        this.currentSlide = 0
      }
    },

    handleTouchStart(event) {
      this.touchStartX = event.touches[0].clientX
      this.isDragging = false
    },

    /**
     * Handle touch move event
     * @param {Object} event - Touch event
     */
    handleTouchMove() {
      this.isDragging = true
    },

    /**
     * Handle touch end event
     * @param {Object} event - Touch event
     */
    handleTouchEnd(event) {
      if (!this.isDragging) {
        return
      }

      this.touchEndX = event.changedTouches[0].clientX
      const swipeThreshold = 50
      const diff = this.touchStartX - this.touchEndX

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          this.nextSlide()
        } else {
          this.previousSlide()
        }
      }

      this.isDragging = false
    },

    /**
     * Navigate to next slide
     */
    nextSlide() {
      if (!this.isTransitioning) {
        this.isTransitioning = true
      }
      this.currentSlide++
    },

    goToSlide(index) {
      if (index >= 0 && index < this.sortedMedia.length) {
        this.isTransitioning = true
        this.currentSlide = index
      }
    },

    getSourceContainsCode(source) {
      if (!this.activeCode && this.isGiftCard) {
        return true
      }

      return isColorCodeInStr(this.activeCode, source)
    },

    /**
     * Build new array based on existence of an index in a target array.
     * @param {Array} arrayOfValues - array that needs to be filtered.
     * @param {Array} arrayOfIndexes - array of indexes to include.
     * @returns {Array}
     */
    filteredArray(arrayOfValues, arrayOfIndexes) {
      return arrayOfValues.filter((_, index) => {
        return arrayOfIndexes.includes(index)
      })
    },

    /**
     * Handle events when a color swatch receives mouse or keyboard focus.
     * - Sets active swatch image and active variant.
     * @param {Object} colorData - Color data.
     */
    handleActiveSwatch(colorData) {
      const isSameProduct = colorData.handle === this.product.handle

      this.activeSwatchImage = isSameProduct
        ? this.product.hoverImages?.find(image => isColorCodeInStr(colorData.code, image?.src))
        : this.matchedProducts?.find(product => product.handle === colorData.handle)?.featuredImage

      if (this.activeSwatchImage) {
        this.loadActiveSwatch = true
      }

      if (this.loadActiveSwatch && !colorData?.initialSwatch) {
        this.showSwatchImage = true
      }

      const activeVariant = this.product.variants
        .find((variant) => isColorCodeInStr(colorData.code, variant.swatchUrl))

      this.setActiveVariant(activeVariant)
    },

    /**
     * Handle events when the product card image container loses focus.
     * - Reset any active swatch image,the secondary image states, and the
     *   active variant.
     */
    handleProductCardBlur() {
      this.activeSwatchImage = {}
      this.showSecondaryImage = false
      this.activeVariant = this.initialVariant
    },

    /**
     * Handle events when the product card image container gains focus.
     * - Remove active swatch image from colour swatch focus, and set the
     *   main secondary image state to true.
     */
    handleProductCardFocus() {
      this.activeSwatchImage = {}
      this.showSecondaryImage = true
    },

    /**
     * Get product card img alt.
     * @param {Number} number
     * @returns {String}
     */
    getImgAlt(number) {
      return this.$string('product.card.img_alt')
        .replace('{{ title }}', this.product.title)
        .replace('{{ number }}', number)
    },

    /**
     * Load products from GraphQL and sort currently active product to front.
     */
    async queryMatchedProducts() {
      if (!this.styleGroupTag) {
        this.setColorOptions()
        return
      }

      const query = `(tag:'${this.styleGroupTag}')`

      try {
        const response = await cnvs.Query({
          query: productsByTagQuery,
          variables: {
            country: this.$variable('store.country'),
            query,
            language: this.$variable('store.language'),
          },
        })

        if (!response.products.edges.length) {
          return
        }

        const tmpSwatches = []

        response.products.edges.forEach((product) => {
          const data = { product: product.node }

          tmpSwatches.push(formatProduct(data))
        })

        const activeProduct = tmpSwatches.find((product) => {
          return this.product.id === product.id
        })

        const index = tmpSwatches.indexOf(activeProduct)

        tmpSwatches.splice(index, 1)
        tmpSwatches.unshift(this.product)

        this.matchedProducts = tmpSwatches

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Set the active variant.
     * @param {Object} variant - variant object.
     */
    setActiveVariant(variant) {
      this.activeVariant = variant
    },

    /**
     * Set array of color options objects, one for each matched product.
     * @returns {Array|Boolean}
     */
    setColorOptions() {
      if (!this.product?.colorOptions || this.product?.giftCard) {
        this.colorOptions = []
        return
      }

      const maxSwatches = this.isMobile ? 1 : 3

      if (!this.matchedProducts?.length) {
        const totalColors = this.product.colorOptions[0]?.colorCodes?.length || 0

        const limitedColorOptions = {
          ...this.product.colorOptions[0],
          productIndex: 0,
          variants: this.product.variants,
        }

        if (totalColors > maxSwatches) {
          limitedColorOptions.colorCodes = this.product.colorOptions[0]
            .colorCodes.slice(0, maxSwatches)
          limitedColorOptions.colorLabels = this.product.colorOptions[0]
            .colorLabels.slice(0, maxSwatches)
          limitedColorOptions.values = this.product.colorOptions[0].values.slice(0, maxSwatches)
        }

        this.colorOptions = [limitedColorOptions]

        if (!this.isMobile && totalColors > maxSwatches) {
          this.renderPlusIcon = true
          this.plusIconCount = totalColors - maxSwatches
        } else {
          this.renderPlusIcon = false
          this.plusIconCount = 0
        }

        return
      }

      const matchedColorOptions = []
      let renderedSwatchCount = 0

      for (const matchedProduct of this.matchedProducts) {
        if (!matchedProduct?.colorOptions?.length) {
          continue
        }

        const colorCodesCount = matchedProduct.colorOptions[0]?.colorCodes?.length || 0
        const remainingSlots = maxSwatches - renderedSwatchCount

        if (remainingSlots <= 0) {
          break
        }

        if (colorCodesCount > remainingSlots) {
          const limitedColorOptions = {
            ...matchedProduct.colorOptions[0],
            productIndex: matchedColorOptions.length,
            variants: matchedProduct.variants,
            colorCodes: matchedProduct.colorOptions[0].colorCodes.slice(0, remainingSlots),
            colorLabels: matchedProduct.colorOptions[0].colorLabels.slice(0, remainingSlots),
            values: matchedProduct.colorOptions[0].values.slice(0, remainingSlots),
          }

          matchedColorOptions.push(limitedColorOptions)
          renderedSwatchCount += remainingSlots
        } else {
          matchedColorOptions.push({
            ...matchedProduct.colorOptions[0],
            productIndex: matchedColorOptions.length,
            variants: matchedProduct.variants,
          })

          renderedSwatchCount += colorCodesCount
        }
      }

      const totalColors = this.matchedProducts.reduce((sum, product) => {
        return sum + (product.colorOptions[0]?.colorCodes?.length || 0)
      }, 0)

      if (!this.isMobile && totalColors > maxSwatches) {
        this.renderPlusIcon = true
        this.plusIconCount = totalColors - maxSwatches
      } else {
        this.renderPlusIcon = false
        this.plusIconCount = 0
      }

      this.colorOptions = matchedColorOptions
    },

    /**
     * Set the initial variant.
     * - Save this data so that the appropriate information is displayed to the
     *   user when the product card loses focus.
     */
    setInitialVariant() {
      const activeCode = this.colorOptions?.colorCodes
        ?.find((code) => isColorCodeInStr(code, this.product.featuredImage?.src))

      this.initialVariant = this.product?.variants
        ?.find((variant) => isColorCodeInStr(activeCode, variant.swatchUrl))
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
